import { Alert, Button, Tooltip } from "antd";
import {
	type HandoffBanner as HandoffBannerState,
	handoffBanner,
	isHandoffOpen,
} from "hooks/chatHandoffState";
import { Headset } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import type { IAssistantHandoff } from "types/assistant-handoff.types";
import "../handoff.css";

interface TalkToPersonButtonProps {
	handoff: IAssistantHandoff | null;
	isRequesting: boolean;
	onRequest: () => void;
	isDisabled?: boolean;
	// Shown as a tooltip (and the wrapper's label) while disabled.
	disabledReason?: string;
}

/**
 * Asks for a person from Equility. Hidden while a request is open, because
 * the banner then carries the state (and the cancel).
 */
export function TalkToPersonButton({
	handoff,
	isRequesting,
	onRequest,
	isDisabled = false,
	disabledReason,
}: TalkToPersonButtonProps) {
	if (isHandoffOpen(handoff)) return null;
	const button = (
		<Button
			type="text"
			className="chat-talk-to-person"
			icon={<Headset size={16} aria-hidden />}
			onClick={onRequest}
			loading={isRequesting}
			disabled={isDisabled}
			aria-label="Talk to a person"
		>
			Talk to a person
		</Button>
	);
	if (!isDisabled || !disabledReason) return button;
	// A disabled button fires no mouse events, so the tooltip hangs off a
	// wrapper; the wrapper also carries the reason for screen readers.
	return (
		<Tooltip title={disabledReason}>
			<span
				className="chat-talk-to-person-wrap"
				aria-label={disabledReason}
				role="group"
			>
				{button}
			</span>
		</Tooltip>
	);
}

interface HandoffBannerProps {
	handoff: IAssistantHandoff | null;
	// Cancels a waiting request, or ends the staff chat once someone joined.
	onCancel: () => void;
	// Newest staff byline in the transcript, used when the row has no name.
	lastStaffName?: string | null;
	// Why the last request for a person did not go through (e.g. rate limit).
	error?: string | null;
}

/**
 * Where the request for a person stands. Decision 1 of the plan: the AI keeps
 * answering while the customer waits, so the waiting copy says so instead of
 * implying the chat has stopped.
 */
export function HandoffBanner({
	handoff,
	onCancel,
	lastStaffName,
	error,
}: HandoffBannerProps) {
	const banner = handoffBanner(handoff, lastStaffName);
	const containerRef = useRef<HTMLDivElement>(null);
	const previousKind = useRef<HandoffBannerState["kind"] | null>(null);
	const kind = banner?.kind ?? null;

	// Pressing "Talk to a person" unmounts that button, which drops keyboard
	// focus to <body>. Hand it to the banner that replaced it, so a keyboard
	// or screen reader user lands on the new state and its Cancel control.
	// Someone typing elsewhere keeps their focus.
	useEffect(() => {
		const hasAppeared = previousKind.current === null && kind !== null;
		previousKind.current = kind;
		if (!hasAppeared) return;
		const active = document.activeElement;
		if (!active || active === document.body) containerRef.current?.focus();
	}, [kind]);

	if (!banner && !error) return null;
	return (
		<div ref={containerRef} tabIndex={-1} className="chat-handoff-banner-focus">
			{error && (
				<Alert
					className="chat-handoff-banner"
					type="warning"
					showIcon
					message={error}
				/>
			)}
			{banner && <BannerAlert banner={banner} onCancel={onCancel} />}
		</div>
	);
}

function BannerAlert({
	banner,
	onCancel,
}: {
	banner: HandoffBannerState;
	onCancel: () => void;
}) {
	if (banner.kind === "waiting") {
		return (
			<Alert
				className="chat-handoff-banner"
				type="info"
				showIcon
				message={
					<div className="chat-handoff-banner-body">
						<span>
							We've asked someone from Equility to join. The assistant keeps
							helping while you wait.
						</span>
						<Button size="small" onClick={onCancel}>
							Cancel request
						</Button>
					</div>
				}
			/>
		);
	}

	if (banner.kind === "active") {
		const who = banner.staffName
			? `${banner.staffName} from Equility`
			: "Someone from Equility";
		return (
			<Alert
				className="chat-handoff-banner"
				type="success"
				showIcon
				message={
					<div className="chat-handoff-banner-body">
						<span>{`${who} is here. The AI assistant is paused.`}</span>
						{/* Ending the staff chat hands it back to the AI (API: DELETE
						    while HUMAN_ACTIVE -> RETURNED_TO_AI). */}
						<Button size="small" onClick={onCancel}>
							Back to assistant
						</Button>
					</div>
				}
			/>
		);
	}

	return (
		<Alert
			className="chat-handoff-banner"
			type="info"
			showIcon
			message={
				<div className="chat-handoff-banner-body">
					<span>
						{banner.ticketRef
							? `No one was free right now, so we opened ticket ${banner.ticketRef}. We'll reply by email.`
							: "No one was free right now, so we opened a support ticket. We'll reply by email."}
					</span>
					{banner.ticketId !== null && (
						<Link to={`/support/${banner.ticketId}`}>View ticket</Link>
					)}
				</div>
			}
		/>
	);
}

/**
 * The server closed this chat (expired, or refused a turn or a handoff with
 * 409). The transcript stays readable; the only way on is a new chat, so the
 * notice carries that action instead of letting sends fail again.
 */
export function SessionEndedNotice({ onNewChat }: { onNewChat: () => void }) {
	return (
		<div role="note" className="chat-system-notice chat-session-ended">
			<span className="chat-system-notice-text">
				This chat ended. Start a new chat to continue.
			</span>
			<Button size="small" type="primary" onClick={onNewChat}>
				New chat
			</Button>
		</div>
	);
}
